/**
 * What this tab should do about another tab's write to the auth token.
 *
 * Extracted from the `storage` listener in `auth.tsx` because the rule is
 * small, entirely a matter of judgement, and has one failure mode that is
 * invisible in review and unpleasant in production: adopting a token while
 * already authenticated makes two tabs write each other's value back and forth
 * forever, since `storage` fires on any `setItem` in a sibling tab — including
 * one that stores a value that tab already holds.
 */
export type CrossTabSessionAction = "ignore" | "sign-out" | "adopt" | "reload";

/**
 * The localStorage key the session token lives under. Not exported: `auth.tsx`
 * and `fetch.ts` still hold their own literals, and unifying those is a wider
 * change than this one.
 */
const AUTH_TOKEN_KEY = "authToken";

export function classifyAuthTokenStorageEvent(event: {
	key: string | null;
	newValue: string | null;
	isAuthenticated: boolean;
	/** The token this tab is currently sending, from `fetch.ts`. */
	currentToken: string | null;
}): CrossTabSessionAction {
	if (event.key !== AUTH_TOKEN_KEY) {
		return "ignore";
	}
	if (event.newValue === null) {
		// A sibling signed out, 401'd, or hit the expiry flip. Mirror it — but
		// only if there is a session here to end; a tab already sitting on the
		// login page has nothing to do and should not toast about it.
		return event.isAuthenticated ? "sign-out" : "ignore";
	}
	// A sibling signed in while this tab has no session: adopt it in place.
	if (!event.isAuthenticated) {
		return "adopt";
	}
	// This tab already sends exactly this token: the loop guard described above.
	if (event.newValue === event.currentToken) {
		return "ignore";
	}
	// A sibling activated a DIFFERENT session — switched organization, or signed
	// in again. Staying on the old token is not an option: the two tabs would act
	// in different tenants under one `authToken`, and when the old session hits
	// its cutoff here, the expiry path clears `authToken` and signs the sibling
	// out of the session it just started. Reload instead, which re-runs the boot
	// path against the new token and rebuilds every socket and cache from it.
	// Not a loop: the reload reads the stored value rather than writing a new one,
	// so the tab that triggered it sees its own token come back and ignores it.
	return "reload";
}
